import React, { useState, useEffect } from 'react';
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import axiosClient from '../api/axiosClient';
import { formatDate } from '../utils/dateUtils';

// Helper to extract multiple 10-digit phone numbers
const extractPhoneNumbers = (input) => {
  if (!input) return [];
  const parts = String(input).split(/[,/;\n\s&]+/);
  const result = [];
  for (const part of parts) {
    let clean = part.replace(/\D/g, '');
    if (clean.length === 12 && clean.startsWith('91')) {
      clean = clean.slice(2);
    } else if (clean.length === 11 && clean.startsWith('0')) {
      clean = clean.slice(1);
    }
    if (clean.length >= 10 && clean.length <= 13) {
      if (!result.includes(clean)) {
        result.push(clean);
      }
    }
  }
  return result;
};

const InvoicePrintModal = ({ show = true, onClose, invoiceData, type = 'sale' }) => {
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [capturingImage, setCapturingImage] = useState(false);
  const [imageCopiedNotice, setImageCopiedNotice] = useState(false);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [capturedImageUrl, setCapturedImageUrl] = useState(null);
  const [sendingApi, setSendingApi] = useState(false);
  const [apiStatusMsg, setApiStatusMsg] = useState('');
  const [targetMobile, setTargetMobile] = useState('');
  const [waSettings, setWaSettings] = useState(null);
  const [sendToCustCheck, setSendToCustCheck] = useState(true);
  const [sendToOwnerCheck, setSendToOwnerCheck] = useState(false);
  const [ownerPhone, setOwnerPhone] = useState('');

  const isSale = String(type).toLowerCase() === 'sale';

  useEffect(() => {
    if (invoiceData) {
      const mob = isSale ? (invoiceData.CustomerMobile || '') : (invoiceData.SupplierMobile || '');
      setTargetMobile(mob || '');
    }
  }, [invoiceData, isSale]);

  useEffect(() => {
    let isMounted = true;
    axiosClient.get('/whatsapp/settings')
      .then((res) => {
        if (isMounted && res.success && res.settings) {
          const s = res.settings;
          setWaSettings(s);
          setSendToCustCheck(s.SendToCustomer !== 0);
          setSendToOwnerCheck(s.SendToOwner === 1);
          setOwnerPhone(s.OwnerMobile || s.firmDefaultContact || '');
        }
      })
      .catch((err) => {
        console.warn('Could not load WhatsApp settings for invoice modal:', err.message);
      });
    return () => { isMounted = false; };
  }, []);

  if (show === false || !invoiceData) return null;

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    const element = document.getElementById('invoice-printable-doc');
    if (!element) return;
    setGeneratingPdf(true);
    try {
      const invNo = isSale ? invoiceData.SaleNo : (invoiceData.purchaseNo || invoiceData.InvoiceNo || 'Doc');
      const partyName = (isSale ? (invoiceData.CustomerName || 'Customer') : (invoiceData.SupplierName || 'Supplier'))
        .replace(/[^a-zA-Z0-9]/g, '_');
      
      const opt = {
        margin: [8, 8, 8, 8],
        filename: `Invoice_${isSale ? 'Sale' : 'Purchase'}_${invNo}_${partyName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error('PDF Generation Error:', err);
      alert('Error generating PDF download. You can also use "Print Invoice" -> "Save as PDF".');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const buildWhatsAppMessage = () => {
    const firm = invoiceData.firmName || 'GORAKH D. LANKE';
    const firmAddress = invoiceData.FirmAddress || 'Alibag / Colaba, Mumbai';
    const firmContact = invoiceData.FirmContact ? `📞 Contact: ${invoiceData.FirmContact}` : '';
    const invNo = isSale ? invoiceData.SaleNo : (invoiceData.purchaseNo || invoiceData.InvoiceNo || '-');
    const date = formatDate(invoiceData.SaleDate || invoiceData.purchaseDate || new Date());
    const partyName = isSale ? invoiceData.CustomerName : invoiceData.SupplierName;
    const items = invoiceData.items || [];

    let msg = `🧾 *${isSale ? 'SALE INVOICE' : 'PURCHASE VOUCHER'}*\n`;
    msg += `🏢 *${firm}*\n`;
    if (firmAddress) msg += `📍 ${firmAddress}\n`;
    if (firmContact) msg += `${firmContact}\n`;
    msg += `--------------------------------\n`;
    msg += `*Invoice No:* #${invNo}\n`;
    msg += `*Date:* ${date}\n`;
    msg += `*${isSale ? 'Customer' : 'Supplier'}:* ${partyName || 'Cash'}\n`;
    if (targetMobile) msg += `*Mobile:* ${targetMobile}\n`;
    if (invoiceData.VehicleNo) msg += `*Vehicle No:* ${invoiceData.VehicleNo}\n`;
    msg += `--------------------------------\n`;
    msg += `*ITEMS:*\n`;

    items.forEach((it, idx) => {
      const name = it.productName || 'Product';
      const qtyStr = isSale && it.noofbox ? `${it.noofbox} Box | ${parseFloat(it.net_qty || it.Qty || 0).toFixed(2)} kg` : `${parseFloat(it.Qty || it.qty || 0).toFixed(2)} ${it.unit || 'KG'}`;
      const rateStr = `₹${parseFloat(it.Rate || it.rate || 0).toFixed(2)}`;
      const totalStr = `₹${parseFloat(it.total || 0).toFixed(2)}`;
      msg += `${idx + 1}. *${name}*\n   ↳ ${qtyStr} @ ${rateStr} = *${totalStr}*\n`;
    });

    msg += `--------------------------------\n`;
    msg += `*Subtotal:* ₹${parseFloat(invoiceData.SubTotal || invoiceData.subtotal || 0).toFixed(2)}\n`;

    if (isSale && parseFloat(invoiceData.HamaliCharges || 0) > 0) {
      msg += `*Loading Charges:* ₹${parseFloat(invoiceData.HamaliCharges).toFixed(2)}\n`;
    }
    if (isSale && parseFloat(invoiceData.IceCharges || 0) > 0) {
      msg += `*Ice Charges:* ₹${parseFloat(invoiceData.IceCharges).toFixed(2)}\n`;
    }
    if (isSale && parseFloat(invoiceData.TransportCharges || 0) > 0) {
      msg += `*Transport Charges:* ₹${parseFloat(invoiceData.TransportCharges).toFixed(2)}\n`;
    }
    if (parseFloat(invoiceData.DiscAmount || 0) > 0) {
      msg += `*Discount:* -₹${parseFloat(invoiceData.DiscAmount).toFixed(2)}\n`;
    }

    const grandTotal = parseFloat(invoiceData.GrandTotal || invoiceData.GranadTotal || 0).toFixed(2);
    msg += `*GRAND TOTAL:* *₹${grandTotal}*\n`;

    if (parseFloat(invoiceData.PaidAmt || 0) > 0) {
      msg += `*Paid Amount:* ₹${parseFloat(invoiceData.PaidAmt).toFixed(2)}\n`;
    }
    if (isSale && invoiceData.previousBalance !== undefined) {
      msg += `*Previous Balance:* ₹${parseFloat(invoiceData.previousBalance).toFixed(2)}\n`;
    }
    if (isSale && invoiceData.netBalanceAfterSale !== undefined) {
      msg += `*Net Outstanding Balance:* *₹${parseFloat(invoiceData.netBalanceAfterSale).toFixed(2)}*\n`;
    }

    if (invoiceData.Remark) {
      msg += `*Remark:* ${invoiceData.Remark}\n`;
    }

    // Direct online view and print bill link for recipient
    const publicUrl = window.location.origin;
    const viewUrl = `${publicUrl}/api/whatsapp/view-invoice?type=${isSale ? 'sale' : 'purchase'}&id=${invNo}`;
    msg += `--------------------------------\n`;
    msg += `📄 *View & Print Bill Online:*\n${viewUrl}\n`;
    msg += `--------------------------------\n`;
    if (waSettings?.CustomMessage) {
      msg += `💬 _${waSettings.CustomMessage}_\n`;
    } else {
      msg += `🙏 _Thank you for your business!_\n`;
    }

    return msg;
  };

  // Capture bill as PNG image blob
  const generateInvoiceImageBlob = async () => {
    const element = document.getElementById('invoice-printable-doc');
    if (!element) return null;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false
    });
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  };

  const copyBlobToClipboard = async (blob) => {
    if (!blob) return false;
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const item = new ClipboardItem({ 'image/png': blob });
        await navigator.clipboard.write([item]);
        return true;
      }
    } catch (err) {
      console.warn('Clipboard write warning:', err);
    }
    return false;
  };

  // Send via WhatsApp Web (Desktop Browser) or Mobile WhatsApp App with Image Copy-Paste
  const handleSendWhatsAppWeb = async () => {
    if (waSettings && (waSettings.IsEnabled === 0 || waSettings.IsEnabled === false)) {
      alert('⚠️ WhatsApp sender is turned OFF in WhatsApp Settings.\n\nPlease enable the WhatsApp Sender in Settings -> WhatsApp Settings.');
      return;
    }

    if (!sendToCustCheck && !sendToOwnerCheck) {
      alert('Please select at least one recipient (Customer or Firm Owner).');
      return;
    }

    const text = buildWhatsAppMessage();
    const cleanCustomer = (targetMobile || '').replace(/\D/g, '');

    const targets = [];
    if (sendToCustCheck && cleanCustomer && cleanCustomer.length >= 10) {
      const p = cleanCustomer.length === 10 ? '91' + cleanCustomer : cleanCustomer;
      targets.push({ type: isSale ? 'Customer' : 'Supplier', phone: p });
    }

    if (sendToOwnerCheck) {
      const ownerNums = extractPhoneNumbers(ownerPhone);
      ownerNums.forEach((num) => {
        const p = num.length === 10 ? '91' + num : num;
        if (!targets.some((t) => t.phone === p)) {
          targets.push({ type: 'Firm Owner', phone: p });
        }
      });
    }

    if (targets.length === 0) {
      if (cleanCustomer) {
        const p = cleanCustomer.length === 10 ? '91' + cleanCustomer : cleanCustomer;
        targets.push({ type: isSale ? 'Customer' : 'Supplier', phone: p });
      } else {
        alert('Please enter a valid 10-digit mobile number.');
        return;
      }
    }

    setCapturingImage(true);
    setApiStatusMsg('📸 Generating bill image and copying to clipboard...');

    try {
      let blob = capturedBlob;
      let imgUrl = capturedImageUrl;
      if (!blob) {
        blob = await generateInvoiceImageBlob();
        if (blob) {
          setCapturedBlob(blob);
          imgUrl = URL.createObjectURL(blob);
          setCapturedImageUrl(imgUrl);
        }
      }

      // Auto-copy bill image into system clipboard
      let copied = false;
      if (blob) {
        copied = await copyBlobToClipboard(blob);
      }

      // Check Mobile Web Share API for 1-tap native image sharing
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      const invNo = isSale ? invoiceData.SaleNo : (invoiceData.purchaseNo || invoiceData.InvoiceNo || 'Doc');

      if (isMobile && navigator.canShare && blob) {
        try {
          const imageFile = new File([blob], `Bill_${invNo}.png`, { type: 'image/png' });
          if (navigator.canShare({ files: [imageFile] })) {
            await navigator.share({
              title: `Bill #${invNo}`,
              text: text,
              files: [imageFile]
            });
            setApiStatusMsg('✅ Bill image shared via WhatsApp App!');
            setCapturingImage(false);
            return;
          }
        } catch (shareErr) {
          console.warn('Native share cancelled or not completed, opening chat link:', shareErr);
        }
      }

      // Open WhatsApp Web or App
      targets.forEach((t, idx) => {
        const waUrl = isMobile
          ? `https://api.whatsapp.com/send?phone=${t.phone}&text=${encodeURIComponent(text)}`
          : `https://web.whatsapp.com/send?phone=${t.phone}&text=${encodeURIComponent(text)}`;
        if (idx === 0) {
          window.open(waUrl, '_blank');
        } else {
          setTimeout(() => {
            window.open(waUrl, '_blank');
          }, 1200);
        }
      });

      setImageCopiedNotice(true);
      setApiStatusMsg(
        copied
          ? '📋 Bill Image Copied to Clipboard! In WhatsApp Web, press Ctrl+V to paste & send image.'
          : '🚀 WhatsApp Web opened!'
      );
    } catch (err) {
      console.error('WhatsApp Web Image Error:', err);
      // Fallback: open WhatsApp Web with text
      targets.forEach((t, idx) => {
        const waUrl = `https://api.whatsapp.com/send?phone=${t.phone}&text=${encodeURIComponent(text)}`;
        if (idx === 0) window.open(waUrl, '_blank');
        else setTimeout(() => window.open(waUrl, '_blank'), 1000);
      });
      setApiStatusMsg('🚀 WhatsApp Web opened!');
    } finally {
      setCapturingImage(false);
    }
  };

  // Send via WhatsApp Business API / Cloud Gateway
  const handleSendViaBusinessApi = async () => {
    if (waSettings && (waSettings.IsEnabled === 0 || waSettings.IsEnabled === false)) {
      alert('⚠️ WhatsApp sender is turned OFF in WhatsApp Settings.\n\nPlease enable the WhatsApp Sender in Settings -> WhatsApp Settings to send messages via API.');
      return;
    }

    if (!sendToCustCheck && !sendToOwnerCheck) {
      alert('Please select at least one recipient (Customer or Firm Owner).');
      return;
    }

    if (sendToCustCheck && !targetMobile) {
      alert('Please enter a valid WhatsApp mobile number for the customer.');
      return;
    }

    setSendingApi(true);
    setApiStatusMsg('');
    try {
      const invNo = isSale ? invoiceData.SaleNo : (invoiceData.purchaseNo || invoiceData.InvoiceNo || 'Doc');
      const partyName = isSale ? (invoiceData.CustomerName || 'Customer') : (invoiceData.SupplierName || 'Supplier');
      const saleDate = invoiceData.SaleDate || invoiceData.purchaseDate || invoiceData.saleDate || new Date().toISOString().slice(0, 10);
      const firmName = invoiceData.firmName || 'FISH SHOP MUMBAI';
      const firmContact = invoiceData.FirmContact || invoiceData.FirmMobile || invoiceData.mobileNo || '';
      const firmInfo = `${firmName} ${firmContact}`.trim();
      const grandTotal = parseFloat(invoiceData.GrandTotal || invoiceData.GranadTotal || 0).toFixed(2);
      const messageText = buildWhatsAppMessage();

      const res = await axiosClient.post('/whatsapp/send-invoice', {
        phone: targetMobile,
        sendToCustomer: sendToCustCheck,
        sendToOwner: sendToOwnerCheck,
        ownerMobile: ownerPhone,
        checkSender: true,
        saleId: invoiceData.Id,
        id: invoiceData.Id,
        saleNo: invoiceData.SaleNo,
        invoiceNo: invNo,
        type: isSale ? 'sale' : 'purchase',
        partyName,
        saleDate,
        firmInfo,
        grandTotal,
        subtotal: invoiceData.SubTotal || invoiceData.subtotal,
        hamali: invoiceData.HamaliCharges,
        ice: invoiceData.IceCharges,
        transport: invoiceData.TransportCharges,
        discount: invoiceData.DiscAmount,
        paidAmt: invoiceData.PaidAmt,
        previousBalance: invoiceData.previousBalance,
        netBalanceAfterSale: invoiceData.netBalanceAfterSale,
        vehicleNo: invoiceData.VehicleNo,
        items: invoiceData.items || [],
        messageText
      });

      if (res.disabled) {
        setApiStatusMsg('⚠️ WhatsApp sender is turned OFF in Settings.');
      } else if (res.success) {
        setApiStatusMsg(`✅ ${res.message || 'Sent via WhatsApp Business API successfully!'}`);
        setTimeout(() => setApiStatusMsg(''), 5000);
      }
    } catch (err) {
      console.error('WhatsApp API Error:', err);
      const errMsg = err.message || 'Failed to send via WhatsApp Business API.';
      setApiStatusMsg(`⚠️ ${errMsg}`);
      if (window.confirm(`${errMsg}\n\nWould you like to open WhatsApp Web / App directly instead?`)) {
        handleSendWhatsAppWeb();
      }
    } finally {
      setSendingApi(false);
    }
  };

  const grandTotal = parseFloat(invoiceData.GrandTotal || invoiceData.GranadTotal || 0);
  const subTotal = parseFloat(invoiceData.SubTotal || invoiceData.subtotal || 0);

  const isWebMode = waSettings?.SendMode === 'web';

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(15, 23, 42, 0.75)', zIndex: 1060 }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px', overflow: 'hidden' }}>
          
          {/* Header Bar */}
          <div className="modal-header bg-dark text-white px-4 py-3 no-print d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary text-uppercase px-2 py-1">
                <i className="bi bi-file-earmark-pdf me-1"></i>
                {isSale ? 'Sale Invoice' : 'Purchase Voucher'} #{isSale ? invoiceData.SaleNo : (invoiceData.purchaseNo || invoiceData.InvoiceNo)}
              </span>
              <h5 className="modal-title mb-0 fw-semibold fs-6">
                Print, PDF & WhatsApp Sharing
              </h5>
            </div>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close"></button>
          </div>

          {/* Quick Actions Bar for WhatsApp & PDF */}
          <div className="bg-light border-bottom p-3 no-print">
            <div className="row g-2 align-items-center">
              
              {/* Left Column: WhatsApp Controls */}
              <div className="col-12 col-md-7">
                <div className="input-group">
                  <span className="input-group-text bg-success text-white fw-semibold">
                    <i className="bi bi-whatsapp me-1"></i> WhatsApp No:
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter 10-digit mobile number"
                    value={targetMobile}
                    onChange={(e) => setTargetMobile(e.target.value)}
                  />

                  {/* WhatsApp Action Buttons according to user preference in Settings */}
                  {isWebMode ? (
                    <>
                      <button
                        className="btn btn-success fw-semibold d-flex align-items-center gap-1 shadow-sm px-3"
                        onClick={handleSendWhatsAppWeb}
                        disabled={capturingImage}
                        title="Open WhatsApp Web on PC or WhatsApp application on Mobile"
                      >
                        {capturingImage ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status"></span>
                            Generating Image...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-whatsapp"></i> Send WhatsApp
                          </>
                        )}
                      </button>
                      <button
                        className="btn btn-outline-primary fw-semibold d-flex align-items-center gap-1"
                        onClick={handleSendViaBusinessApi}
                        disabled={sendingApi}
                        title="Send via configured WhatsApp API Gateway"
                      >
                        {sendingApi ? <span className="spinner-border spinner-border-sm" role="status"></span> : <i className="bi bi-cloud-arrow-up-fill"></i>}
                        API
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn-primary fw-semibold d-flex align-items-center gap-1 shadow-sm px-3"
                        onClick={handleSendViaBusinessApi}
                        disabled={sendingApi}
                        title="Send via configured WhatsApp Business API"
                      >
                        {sendingApi ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status"></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-send-check-fill"></i> Send WhatsApp
                          </>
                        )}
                      </button>
                      <button
                        className="btn btn-outline-success fw-semibold d-flex align-items-center gap-1"
                        onClick={handleSendWhatsAppWeb}
                        disabled={capturingImage}
                        title="Open in WhatsApp Web / App"
                      >
                        {capturingImage ? <span className="spinner-border spinner-border-sm" role="status"></span> : <i className="bi bi-whatsapp"></i>}
                        Web
                      </button>
                    </>
                  )}
                </div>

                {/* Recipient Checkboxes & Settings Status */}
                <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
                  <div className="form-check form-check-inline mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="modalSendCust"
                      checked={sendToCustCheck}
                      onChange={(e) => setSendToCustCheck(e.target.checked)}
                    />
                    <label className="form-check-label small fw-semibold cursor-pointer" htmlFor="modalSendCust">
                      {isSale ? 'Customer' : 'Supplier'} ({targetMobile || 'None'})
                    </label>
                  </div>
                  <div className="form-check form-check-inline mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="modalSendOwner"
                      checked={sendToOwnerCheck}
                      onChange={(e) => setSendToOwnerCheck(e.target.checked)}
                    />
                    <label className="form-check-label small fw-semibold text-primary cursor-pointer" htmlFor="modalSendOwner">
                      Firm Owner ({ownerPhone || 'None'})
                    </label>
                  </div>
                  {waSettings && (waSettings.IsEnabled === 0 || waSettings.IsEnabled === false) && (
                    <span className="badge bg-danger bg-opacity-10 text-danger border border-danger small py-1">
                      <i className="bi bi-x-circle me-1"></i> Sender: OFF
                    </span>
                  )}
                  {waSettings && waSettings.IsEnabled && (
                    <span className="badge bg-light text-muted border small py-1">
                      Mode: {isWebMode ? 'WhatsApp Web/App' : 'WhatsApp API'}
                    </span>
                  )}
                </div>

                {apiStatusMsg && (
                  <div className="small mt-1 fw-semibold text-truncate text-primary" style={{ fontSize: '0.85rem' }}>
                    {apiStatusMsg}
                  </div>
                )}
              </div>

              {/* Right Column: PDF Download & Print */}
              <div className="col-12 col-md-5 d-flex justify-content-md-end gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-danger fw-semibold d-flex align-items-center gap-1 shadow-sm"
                  onClick={handleDownloadPdf}
                  disabled={generatingPdf}
                >
                  {generatingPdf ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-file-earmark-pdf-fill"></i> Download PDF
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-primary fw-semibold d-flex align-items-center gap-1 shadow-sm"
                  onClick={handlePrint}
                >
                  <i className="bi bi-printer-fill"></i> Print / Save PDF
                </button>
              </div>
            </div>
          </div>

          {/* Alert Banner: Bill Image Copied to Clipboard */}
          {imageCopiedNotice && (
            <div className="bg-success bg-opacity-10 border-bottom border-success px-4 py-2 d-flex justify-content-between align-items-center flex-wrap gap-2 no-print">
              <div className="d-flex align-items-center gap-2">
                <span className="fs-4 text-success">📋</span>
                <div>
                  <div className="fw-bold text-success">Bill Image Copied to Clipboard!</div>
                  <div className="small text-dark">
                    In WhatsApp Web, click the message box and press <strong>Ctrl + V</strong> (or Right-Click → Paste) to send the bill image directly.
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-success fw-semibold shadow-sm"
                  onClick={async () => {
                    if (capturedBlob) {
                      await copyBlobToClipboard(capturedBlob);
                      alert('✅ Bill image re-copied to clipboard! In WhatsApp Web, press Ctrl + V to paste.');
                    }
                  }}
                  title="Re-copy bill image to clipboard"
                >
                  <i className="bi bi-clipboard-check me-1"></i> Re-copy Image
                </button>
                {capturedImageUrl && (
                  <a
                    href={capturedImageUrl}
                    download={`Bill_${isSale ? invoiceData.SaleNo : (invoiceData.purchaseNo || 'Doc')}.png`}
                    className="btn btn-sm btn-outline-success fw-semibold"
                    title="Download PNG Image of Bill"
                  >
                    <i className="bi bi-download me-1"></i> Download Image
                  </a>
                )}
                <button
                  type="button"
                  className="btn-close btn-sm"
                  onClick={() => setImageCopiedNotice(false)}
                  aria-label="Close"
                ></button>
              </div>
            </div>
          )}

          {/* Modal Body: The Printable Invoice Document */}
          <div className="modal-body p-4 bg-secondary bg-opacity-10 d-flex justify-content-center">
            
            <div
              id="invoice-printable-doc"
              className="print-area bg-white p-4 p-md-5 shadow-sm text-dark"
              style={{
                width: '100%',
                maxWidth: '850px',
                minHeight: '1050px',
                borderRadius: '6px',
                fontFamily: `'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif`,
                color: '#1e293b'
              }}
            >
              {/* Top Decorative Border */}
              <div style={{ height: '4px', backgroundColor: '#0284c7', marginBottom: '16px', borderRadius: '2px' }}></div>

              {/* Firm Header */}
              <div className="row pb-3 mb-3 border-bottom align-items-center">
                <div className="col-8">
                  <h2 className="fw-bold mb-1 text-primary" style={{ letterSpacing: '0.5px' }}>
                    {invoiceData.firmName || 'GORAKH D. LANKE'}
                  </h2>
                  <p className="mb-0 text-muted small">
                    {invoiceData.FirmAddress || 'Wholesale & Retail Fish Merchant, Alibag / Colaba, Mumbai'}
                  </p>
                  <p className="mb-0 text-muted small">
                    {invoiceData.FirmContact && <span><strong>Contact:</strong> {invoiceData.FirmContact} &nbsp;|&nbsp; </span>}
                    {invoiceData.FirmEmail && <span><strong>Email:</strong> {invoiceData.FirmEmail} &nbsp;|&nbsp; </span>}
                    {invoiceData.VatNo && <span><strong>GST/VAT No:</strong> {invoiceData.VatNo}</span>}
                  </p>
                </div>
                <div className="col-4 text-end">
                  <div className="badge bg-primary fs-6 px-3 py-2 text-uppercase mb-2">
                    {isSale ? 'TAX / SALE INVOICE' : 'PURCHASE VOUCHER'}
                  </div>
                  <div className="text-muted small">
                    <strong>{isSale ? 'Invoice #:' : 'Voucher #:'}</strong> <span className="fs-6 text-dark fw-bold">#{isSale ? invoiceData.SaleNo : (invoiceData.purchaseNo || invoiceData.InvoiceNo || invoiceData.Id || invoiceData.PurchaseId)}</span>
                  </div>
                  <div className="text-muted small">
                    <strong>Date:</strong> {formatDate(invoiceData.SaleDate || invoiceData.purchaseDate || new Date())}
                  </div>
                </div>
              </div>

              {/* Bill Details Box */}
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <div className="p-3 border rounded h-100 bg-light bg-opacity-50">
                    <div className="text-uppercase text-muted fw-bold small mb-1" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                      {isSale ? 'Billed To (Customer):' : 'Billed From (Supplier):'}
                    </div>
                    <div className="fw-bold fs-6 text-dark">
                      {isSale ? (invoiceData.CustomerName || 'Cash Sale') : (invoiceData.SupplierName || 'Supplier')}
                    </div>
                    {invoiceData.CustomerAddress && (
                      <div className="small text-muted mt-1">
                        <i className="bi bi-geo-alt me-1"></i>{invoiceData.CustomerAddress}
                      </div>
                    )}
                    {(invoiceData.CustomerMobile || invoiceData.SupplierMobile || targetMobile) && (
                      <div className="small text-muted mt-1">
                        <i className="bi bi-telephone me-1"></i>{invoiceData.CustomerMobile || invoiceData.SupplierMobile || targetMobile}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-6">
                  <div className="p-3 border rounded h-100 bg-light bg-opacity-50">
                    <div className="text-uppercase text-muted fw-bold small mb-1" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                      {isSale ? 'Invoice Information:' : 'Voucher Information:'}
                    </div>
                    <div className="row g-1 small">
                      <div className="col-6 text-muted">{isSale ? 'Invoice No:' : 'Voucher No:'}</div>
                      <div className="col-6 text-end fw-semibold">#{isSale ? invoiceData.SaleNo : (invoiceData.purchaseNo || invoiceData.InvoiceNo || invoiceData.Id || invoiceData.PurchaseId)}</div>
                      
                      <div className="col-6 text-muted">Invoice Date:</div>
                      <div className="col-6 text-end fw-semibold">{formatDate(invoiceData.SaleDate || invoiceData.purchaseDate || new Date())}</div>

                      {invoiceData.VehicleNo && (
                        <>
                          <div className="col-6 text-muted">Vehicle No:</div>
                          <div className="col-6 text-end fw-semibold">{invoiceData.VehicleNo}</div>
                        </>
                      )}

                      <div className="col-6 text-muted">Payment Mode:</div>
                      <div className="col-6 text-end fw-semibold text-primary">{parseFloat(invoiceData.PaidAmt || 0) >= grandTotal ? 'Paid (Full)' : (parseFloat(invoiceData.PaidAmt || 0) > 0 ? 'Partially Paid' : 'Credit / Pending')}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Line Items Table */}
              <div className="table-responsive mb-3">
                <table className="table table-bordered table-sm align-middle mb-0" style={{ borderColor: '#cbd5e1' }}>
                  <thead style={{ backgroundColor: '#f1f5f9' }}>
                    <tr className="text-center small fw-bold text-dark">
                      <th style={{ width: '35px' }}>#</th>
                      <th className="text-start">Product / Item Description</th>
                      {isSale && <th style={{ width: '60px' }}>Boxes</th>}
                      {isSale && <th style={{ width: '65px' }}>Kg/Box</th>}
                      {isSale && <th style={{ width: '65px' }}>Add Qty</th>}
                      <th style={{ width: '85px' }}>Gross Wt</th>
                      {isSale && <th style={{ width: '85px' }}>Net Qty</th>}
                      <th className="text-end" style={{ width: '90px' }}>Rate (₹)</th>
                      <th className="text-end" style={{ width: '110px' }}>Total Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invoiceData.items || []).map((it, idx) => (
                      <tr key={idx} className="small" style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa' }}>
                        <td className="text-center text-muted">{idx + 1}</td>
                        <td className="fw-semibold text-dark">{it.productName}</td>
                        {isSale && <td className="text-center fw-bold">{it.noofbox || '-'}</td>}
                        {isSale && <td className="text-center">{it.kgperbox ? parseFloat(it.kgperbox).toFixed(2) : '-'}</td>}
                        {isSale && <td className="text-center text-muted">{it.addqty ? parseFloat(it.addqty).toFixed(2) : '0'}</td>}
                        <td className="text-center">{parseFloat(it.Qty || it.qty || 0).toFixed(2)}</td>
                        {isSale && <td className="text-center fw-bold text-dark">{parseFloat(it.net_qty || it.Qty || 0).toFixed(2)}</td>}
                        <td className="text-end text-dark">₹{parseFloat(it.Rate || it.rate || 0).toFixed(2)}</td>
                        <td className="text-end fw-bold text-dark">₹{parseFloat(it.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }} className="small">
                      <td colSpan={isSale ? 5 : 2} className="text-end text-uppercase">Total Quantity:</td>
                      <td className="text-center">
                        {(invoiceData.items || []).reduce((sum, i) => sum + (parseFloat(i.Qty || i.qty || 0)), 0).toFixed(2)}
                      </td>
                      {isSale && (
                        <td className="text-center text-primary">
                          {(invoiceData.items || []).reduce((sum, i) => sum + (parseFloat(i.net_qty || i.Qty || 0)), 0).toFixed(2)}
                        </td>
                      )}
                      <td className="text-end">Subtotal:</td>
                      <td className="text-end text-dark">₹{subTotal.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Calculations and Balance Summary */}
              <div className="row g-3 pt-2">
                <div className="col-6">
                  {invoiceData.Remark && (
                    <div className="p-3 border rounded bg-light small mb-3">
                      <strong>Remarks / Notes:</strong>
                      <div className="text-muted mt-1">{invoiceData.Remark}</div>
                    </div>
                  )}

                  <div className="p-3 border rounded bg-light bg-opacity-50 small">
                    <div className="fw-bold mb-1 text-dark">Terms & Conditions:</div>
                    <ol className="mb-0 ps-3 text-muted" style={{ fontSize: '11px' }}>
                      <li>Goods once sold will not be taken back or exchanged.</li>
                      <li>Payment is due within agreed settlement credit period.</li>
                      <li>Subject to local jurisdiction only.</li>
                    </ol>
                  </div>
                </div>

                <div className="col-6">
                  <div className="border rounded p-3 bg-light bg-opacity-30">
                    <table className="table table-sm table-borderless small mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted">Item Subtotal:</td>
                          <td className="text-end fw-semibold">₹{subTotal.toFixed(2)}</td>
                        </tr>

                        {isSale && parseFloat(invoiceData.HamaliCharges || 0) > 0 && (
                          <tr>
                            <td className="text-muted">Loading Charges:</td>
                            <td className="text-end">+₹{parseFloat(invoiceData.HamaliCharges).toFixed(2)}</td>
                          </tr>
                        )}

                        {isSale && parseFloat(invoiceData.IceCharges || 0) > 0 && (
                          <tr>
                            <td className="text-muted">Ice Charges:</td>
                            <td className="text-end">+₹{parseFloat(invoiceData.IceCharges).toFixed(2)}</td>
                          </tr>
                        )}

                        {isSale && parseFloat(invoiceData.TransportCharges || 0) > 0 && (
                          <tr>
                            <td className="text-muted">Transport / Freight:</td>
                            <td className="text-end">+₹{parseFloat(invoiceData.TransportCharges).toFixed(2)}</td>
                          </tr>
                        )}

                        {parseFloat(invoiceData.DiscAmount || 0) > 0 && (
                          <tr>
                            <td className="text-danger">Special Discount:</td>
                            <td className="text-end text-danger fw-semibold">-₹{parseFloat(invoiceData.DiscAmount).toFixed(2)}</td>
                          </tr>
                        )}

                        <tr className="border-top border-bottom" style={{ borderColor: '#94a3b8' }}>
                          <td className="fw-bold fs-6 text-dark py-2">GRAND TOTAL:</td>
                          <td className="text-end fw-bold fs-5 text-primary py-2">
                            ₹{grandTotal.toFixed(2)}
                          </td>
                        </tr>

                        {parseFloat(invoiceData.PaidAmt || 0) > 0 && (
                          <tr>
                            <td className="text-success fw-semibold pt-2">Amount Paid:</td>
                            <td className="text-end text-success fw-bold pt-2">₹{parseFloat(invoiceData.PaidAmt).toFixed(2)}</td>
                          </tr>
                        )}

                        {isSale && invoiceData.previousBalance !== undefined && (
                          <tr>
                            <td className="text-muted">Previous Balance:</td>
                            <td className="text-end text-muted">₹{parseFloat(invoiceData.previousBalance).toFixed(2)}</td>
                          </tr>
                        )}

                        {isSale && invoiceData.netBalanceAfterSale !== undefined && (
                          <tr className="border-top fw-bold" style={{ borderColor: '#cbd5e1' }}>
                            <td className="text-dark">Current Net Outstanding:</td>
                            <td className="text-end text-danger fs-6">₹{parseFloat(invoiceData.netBalanceAfterSale).toFixed(2)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Clean Footer Note without signatures */}
              <div className="border-top mt-5 pt-3 text-center small text-muted">
                <em>Thank you for your business!</em>
              </div>

            </div>

          </div>

          {/* Footer Bar */}
          <div className="modal-footer bg-light px-4 py-2 no-print d-flex justify-content-between">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-success d-flex align-items-center gap-1"
                onClick={isWebMode ? handleSendWhatsAppWeb : handleSendViaBusinessApi}
              >
                <i className="bi bi-whatsapp"></i> Send WhatsApp
              </button>
              <button
                type="button"
                className="btn btn-danger d-flex align-items-center gap-1"
                onClick={handleDownloadPdf}
                disabled={generatingPdf}
              >
                <i className="bi bi-file-earmark-pdf"></i> Download PDF
              </button>
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-1"
                onClick={handlePrint}
              >
                <i className="bi bi-printer"></i> Print Invoice
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default InvoicePrintModal;
